/**
 * useProximity — Proximity detection hook for QRScreen
 *
 * Extracted from QRScreen.js (Step 3 of qr-screen-refactor).
 * Self-contained state + lifecycle for proximity-based PXP discovery.
 * Manages: nearby user detection, invite/decline flow, PXP bridge,
 * and channel cleanup.
 *
 * Reference pattern: useHeartbeat.js (same hooks/ directory).
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  joinProximityChannel,
  sendProximityInvite,
  sendProximityDecline,
  sendProximityWithdraw,
  leaveProximityChannel,
  addDismissedPeer,
} from '../services/proximity/proximityService';
import {
  ensureNearbyTiming,
  noteNearby,
  endNearbyTiming,
} from '../services/proximity/nearbyTiming';

/**
 * @param {Object} config
 * @param {string} config.profileUserId
 * @param {boolean} config.isFullyReady - True when all QRScreen systems are initialized
 * @param {Function} config.onPXPScan - Called with constructed URL when guest role triggers PXP
 *
 * @returns {Object}
 * @returns {string} proximityState - 'idle' | 'nearby_detected' | 'invite_sent' | 'connecting'
 * @returns {Object|null} nearbyUser - Closest non-dismissed nearby user
 * @returns {Object|null} proximityCoords - GPS coords for proximity channel
 * @returns {Function} setProximityCoords - Set GPS coords (called by QRScreen on location fetch)
 * @returns {Function} onConnect - Handler for ProximityCard connect button
 * @returns {Function} onDismiss - Handler for ProximityCard dismiss button
 * @returns {Function} invite - the new code page's tap: (peerId) → Promise<answer>
 * @returns {Function} withdraw - the new code page's waiting X: (peerId) → Promise
 * @returns {string|null} waitingOn - who my live request is to (server, 055)
 * @returns {Function} resetProximity - Tear down + bump reset key (for overlay reopen)
 * @returns {Function} cleanupProximity - Full teardown including coords (for overlay close)
 */
const useProximity = ({ profileUserId, isFullyReady, onPXPScan }) => {
  // ─── State ─────────────────────────────────────────────────────────────────
  const [proximityState, setProximityState] = useState('idle');
  const [nearbyUser, setNearbyUser] = useState(null);
  const [proximityCoords, setProximityCoords] = useState(null);
  const [proximityResetKey, setProximityResetKey] = useState(0);
  // Who my live nearby request is to, as the server last said (055; the new
  // code page reads it to learn its request was cancelled)
  const [waitingOn, setWaitingOn] = useState(null);
  const proximityCleanupRef = useRef(null);
  const dismissedUsersRef = useRef(new Set());
  const inviteTimeoutRef = useRef(null);
  // The card's person and state, readable from the server callbacks
  const nearbyUserRef = useRef(null);
  const proximityStateRef = useRef('idle');
  useEffect(() => {
    nearbyUserRef.current = nearbyUser;
    proximityStateRef.current = proximityState;
  }, [nearbyUser, proximityState]);

  // ─── PXP bridge ────────────────────────────────────────────────────────────
  // Deterministic role: lower profileUserId string = Host.
  // Guest calls onPXPScan with constructed URL. Host waits for incoming profile_exchange.
  const handleProximityPXP = useCallback(
    async (otherProfileUserId) => {
      if (!profileUserId) return;

      // Deterministic role assignment
      const iAmHost = profileUserId < otherProfileUserId;
      console.log(
        `[PROXIMITY] PXP bridge — I am ${iAmHost ? 'HOST' : 'GUEST'}, other: ${otherProfileUserId}`
      );

      if (!iAmHost) {
        // Guest: trigger PXP via onPXPScan with constructed URL.
        // Deliberately NO variant QR tag here (variant-rail census leave):
        // this URL is consumed in-app by onPXPScan on this same device —
        // never a page load, so scan-carries-variant doesn't apply.
        try {
          const url = `${window.location.origin}/u/${otherProfileUserId}`;
          await onPXPScan(url);
        } catch (err) {
          console.error('[PROXIMITY] PXP bridge failed:', err);
          setProximityState('error');
        }
      }
      // Host: do nothing — existing handleMessage on session channel handles incoming profile_exchange
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileUserId] // onPXPScan is component-level, stable — not a dep
  );

  // ─── Initialization ────────────────────────────────────────────────────────
  // Starts server-side proximity (own position only — open-access lockdown).
  // Called after location resolves (both on init and mid-session permission grant).
  const initializeProximity = useCallback(
    (coords) => {
      if (!profileUserId || !isFullyReady) return;

      // Reset local dismissed ref (module-level set cleared on leave, not here)
      dismissedUsersRef.current = new Set();
      ensureNearbyTiming();
      noteNearby('nearby_started');

      proximityCleanupRef.current = joinProximityChannel(
        {
          profileUserId,
          lat: coords.latitude,
          lng: coords.longitude,
          accuracy: coords.accuracy ?? null,
        },
        {
          onNearbyUsersChanged: (users) => {
            // Filter out dismissed users
            const filtered = users.filter((u) => !dismissedUsersRef.current.has(u.profileUserId));
            const closest = filtered[0] || null;

            // The person I tapped Connect on has left (locked, backgrounded,
            // closed their QR screen) — both must be looking for a match
            // (Mark, 2026-10-11): clear the waiting card now, not after 30 s.
            // (Only while waiting: after a match they leave on purpose.)
            const tapped = nearbyUserRef.current;
            if (
              proximityStateRef.current === 'invite_sent' &&
              tapped &&
              !users.some((u) => u.profileUserId === tapped.profileUserId)
            ) {
              if (inviteTimeoutRef.current) {
                clearTimeout(inviteTimeoutRef.current);
                inviteTimeoutRef.current = null;
              }
              proximityStateRef.current = 'idle';
              setProximityState('idle');
              if (!closest) setNearbyUser(null);
            }

            if (closest) {
              noteNearby('nearby_seen');
              setNearbyUser(closest);
              setProximityState((prev) =>
                prev === 'idle' || prev === 'nearby_detected' ? 'nearby_detected' : prev
              );
            } else {
              // No one nearby — reset only if we were just detecting
              setProximityState((prev) => {
                if (prev === 'nearby_detected') {
                  setNearbyUser(null);
                  return 'idle';
                }
                return prev;
              });
            }
          },
          onMutualMatch: (otherProfileUserId) => {
            noteNearby('nearby_matched');
            if (inviteTimeoutRef.current) {
              clearTimeout(inviteTimeoutRef.current);
              inviteTimeoutRef.current = null;
            }
            setProximityState('connecting');
            handleProximityPXP(otherProfileUserId);

            // Safety timeout: if PXP doesn't navigate away within 15s
            // (e.g., session lookup failed silently), reset the card
            inviteTimeoutRef.current = setTimeout(() => {
              setProximityState((prev) => {
                if (prev !== 'connecting') return prev;
                console.warn('[PROXIMITY] PXP bridge timed out — resetting');
                setNearbyUser(null);
                return 'idle';
              });
            }, 15000);
          },
          onWaitingOn: setWaitingOn,
          onDeclineReceived: (senderProfileUserId, isProactive) => {
            // Dismiss our card. For real dismissals, add to module-level dismissedPeers
            // (survives across sessions). For proactive re-declines (other user still has
            // us dismissed from a previous session), only add to component-level ref
            // (cleared on re-init) so "both navigate away and back" gives a fresh start.
            if (senderProfileUserId) {
              if (!isProactive) {
                addDismissedPeer(senderProfileUserId);
              }
              dismissedUsersRef.current.add(senderProfileUserId);
            }
            if (inviteTimeoutRef.current) {
              clearTimeout(inviteTimeoutRef.current);
              inviteTimeoutRef.current = null;
            }
            setProximityState('idle');
            setNearbyUser(null);
          },
        }
      );

      console.log('[QR_SCREEN] Proximity initialized');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileUserId, isFullyReady]
  );

  // Effect: initialize proximity when both GPS coords and system readiness are available.
  // proximityResetKey forces re-run on QR re-entry (clearing the ref alone doesn't
  // trigger effects since refs aren't deps).
  useEffect(() => {
    if (!isFullyReady || !proximityCoords || proximityCleanupRef.current) return;
    initializeProximity(proximityCoords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullyReady, proximityCoords, initializeProximity, proximityResetKey]);

  // ─── Button handlers ───────────────────────────────────────────────────────
  const onConnect = useCallback(() => {
    if (!nearbyUser) return;
    noteNearby('nearby_tap');
    setProximityState('invite_sent');

    sendProximityInvite(profileUserId, nearbyUser.profileUserId);

    // 30s timeout — dismiss card if no mutual match
    if (inviteTimeoutRef.current) clearTimeout(inviteTimeoutRef.current);
    inviteTimeoutRef.current = setTimeout(() => {
      setProximityState((prev) => {
        if (prev !== 'invite_sent') return prev;
        setNearbyUser(null);
        return 'idle';
      });
    }, 30000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearbyUser, profileUserId]);

  const onDismiss = useCallback(() => {
    // Broadcast decline so the other user's card dismisses too
    if (nearbyUser && profileUserId) {
      sendProximityDecline(profileUserId, nearbyUser.profileUserId);
      addDismissedPeer(nearbyUser.profileUserId);
      dismissedUsersRef.current.add(nearbyUser.profileUserId);
    }
    if (inviteTimeoutRef.current) {
      clearTimeout(inviteTimeoutRef.current);
      inviteTimeoutRef.current = null;
    }
    setProximityState('idle');
    setNearbyUser(null);
  }, [nearbyUser, profileUserId]);

  // ─── The new code page's taps (E3b-2a) ─────────────────────────────────────
  // The sheet holds the choice; the request goes to the server, which answers
  // 'sent' · 'matched' · 'busy' · 'not_nearby' (or null). A match runs the same
  // bridge as ever (onMutualMatch → the guest scans).
  const invite = useCallback(
    (peerId) => {
      noteNearby('nearby_tap');
      return sendProximityInvite(profileUserId, peerId, { held: true });
    },
    [profileUserId]
  );
  // The waiting X: take my request back, quietly (F5)
  const withdraw = useCallback(
    (peerId) => sendProximityWithdraw(profileUserId, peerId),
    [profileUserId]
  );

  // ─── Cleanup functions (called by QRScreen's open/close effect) ────────────

  // Reset for overlay reopen — tear down old channel, bump reset key to re-init
  const resetProximity = useCallback(() => {
    if (proximityCleanupRef.current) {
      leaveProximityChannel();
      proximityCleanupRef.current = null;
      setProximityState('idle');
      setNearbyUser(null);
      if (inviteTimeoutRef.current) {
        clearTimeout(inviteTimeoutRef.current);
        inviteTimeoutRef.current = null;
      }
      setProximityResetKey((k) => k + 1);
    }
  }, []);

  // Full teardown for overlay close — includes coords reset
  const cleanupProximity = useCallback(() => {
    // Closed without connecting (a landed connect already handed its timing off)
    endNearbyTiming();
    leaveProximityChannel();
    if (proximityCleanupRef.current) {
      proximityCleanupRef.current = null;
    }
    if (inviteTimeoutRef.current) {
      clearTimeout(inviteTimeoutRef.current);
      inviteTimeoutRef.current = null;
    }
    setProximityState('idle');
    setNearbyUser(null);
    setWaitingOn(null);
    setProximityCoords(null);
  }, []);

  return {
    proximityState,
    nearbyUser,
    proximityCoords,
    setProximityCoords,
    onConnect,
    onDismiss,
    invite,
    withdraw,
    waitingOn,
    resetProximity,
    cleanupProximity,
  };
};

export default useProximity;
